import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiBarChart2, FiSettings, FiCheck } = FiIcons;

const CookieSettings = () => {
  const { lang } = useLanguage();
  const isDE = lang === 'de';

  const [saved, setSaved] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('cookiePrefs'));
      if (stored) setPrefs({ ...stored, necessary: true });
    } catch (_) {}
  }, []);

  const toggle = (key) => {
    if (key === 'necessary') return;
    setPrefs(p => ({ ...p, [key]: !p[key] }));
    setSaved(false);
  };

  const save = () => {
    localStorage.setItem('cookiePrefs', JSON.stringify(prefs));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const cookies = [
    {
      key: 'necessary',
      icon: FiShield,
      color: 'green',
      title: isDE ? 'Technisch notwendig' : 'Strictly Necessary',
      basis: isDE ? 'Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO' : 'Legal basis: Art. 6(1)(b) GDPR',
      desc: isDE
        ? 'Diese Cookies sind für den Betrieb der Website unbedingt erforderlich. Sie ermöglichen grundlegende Funktionen wie Seitennavigation, Sicherheit und den Zugang zu gesicherten Bereichen. Die Website kann ohne diese Cookies nicht richtig funktionieren.'
        : 'These cookies are essential for the website to function properly. They enable basic features such as page navigation, security, and access to secure areas. The website cannot function correctly without these cookies.',
      examples: isDE ? 'Sitzungs-ID, CSRF-Token, Authentifizierungstoken' : 'Session ID, CSRF token, authentication token',
      required: true
    },
    {
      key: 'analytics',
      icon: FiBarChart2,
      color: 'blue',
      title: isDE ? 'Analyse & Performance' : 'Analytics & Performance',
      basis: isDE ? 'Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)' : 'Legal basis: Art. 6(1)(a) GDPR (Consent)',
      desc: isDE
        ? 'Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren, indem sie Informationen anonym sammeln und melden. Wir nutzen diese Daten, um die Nutzererfahrung zu verbessern.'
        : 'These cookies help us understand how visitors interact with the website by collecting and reporting information anonymously. We use this data to improve the user experience.',
      examples: isDE ? 'Seitenaufrufe, Sitzungsdauer, Absprungrate' : 'Page views, session duration, bounce rate',
      required: false
    },
    {
      key: 'marketing',
      icon: FiSettings,
      color: 'purple',
      title: isDE ? 'Marketing & Werbung' : 'Marketing & Advertising',
      basis: isDE ? 'Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)' : 'Legal basis: Art. 6(1)(a) GDPR (Consent)',
      desc: isDE
        ? 'Diese Cookies werden verwendet, um Werbung relevanter für dich zu gestalten. Sie werden auch verwendet, um die Anzahl der Anzeigen zu begrenzen und die Effektivität von Werbekampagnen zu messen.'
        : 'These cookies are used to make advertising more relevant to you. They are also used to limit the number of times you see an ad and measure the effectiveness of advertising campaigns.',
      examples: isDE ? 'Retargeting, Conversion-Tracking, Interessenprofile' : 'Retargeting, conversion tracking, interest profiles',
      required: false
    }
  ];

  const colorMap = {
    green: { bg: 'bg-green-100', text: 'text-green-600', ring: 'ring-green-200', badge: 'bg-green-100 text-green-700' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', ring: 'ring-blue-200', badge: 'bg-blue-100 text-blue-700' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', ring: 'ring-purple-200', badge: 'bg-purple-100 text-purple-700' }
  };

  return (
    <div className="bg-gray-50 py-16 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="mb-10">
            <span className="inline-block bg-gray-200 text-gray-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Cookies</span>
            <h1 className="text-4xl font-black text-gray-900 mb-3">
              {isDE ? 'Cookie-Einstellungen' : 'Cookie Settings'}
            </h1>
            <p className="text-gray-600 leading-relaxed max-w-2xl">
              {isDE
                ? 'Wir verwenden Cookies, um dein Erlebnis zu verbessern. Technisch notwendige Cookies können nicht deaktiviert werden. Alle anderen Cookies werden nur mit deiner ausdrücklichen Einwilligung gesetzt (Art. 6 Abs. 1 lit. a DSGVO).'
                : 'We use cookies to enhance your experience. Strictly necessary cookies cannot be disabled. All other cookies are only set with your explicit consent (Art. 6(1)(a) GDPR).'}
            </p>
          </div>

          <div className="space-y-4 mb-8">
            {cookies.map((cookie, index) => {
              const c = colorMap[cookie.color];
              const Icon = cookie.icon;
              const active = prefs[cookie.key];
              return (
                <motion.div
                  key={cookie.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <SafeIcon icon={Icon} className={`${c.text} text-lg`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-base font-bold text-gray-900">{cookie.title}</h3>
                          {cookie.required && (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.badge}`}>
                              {isDE ? 'Erforderlich' : 'Required'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mb-2 font-medium">{cookie.basis}</p>
                        <p className="text-sm text-gray-600 leading-relaxed mb-2">{cookie.desc}</p>
                        <p className="text-xs text-gray-400">
                          <span className="font-semibold">{isDE ? 'Beispiele: ' : 'Examples: '}</span>
                          {cookie.examples}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggle(cookie.key)}
                      disabled={cookie.required}
                      className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 ${c.ring} ${
                        active ? `${c.bg.replace('100', '500')} bg-opacity-80` : 'bg-gray-200'
                      } ${cookie.required ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
                      style={{ backgroundColor: active ? (cookie.color === 'green' ? '#22c55e' : cookie.color === 'blue' ? '#3b82f6' : '#a855f7') : '#e5e7eb' }}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${active ? 'translate-x-6' : 'translate-x-0'}`} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={save}
              className="flex-1 bg-gray-900 text-white py-3 px-6 rounded-xl font-bold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
            >
              {saved ? <><SafeIcon icon={FiCheck} />{isDE ? 'Gespeichert!' : 'Saved!'}</> : (isDE ? 'Einstellungen speichern' : 'Save Preferences')}
            </button>
            <button
              onClick={() => { setPrefs({ necessary: true, analytics: true, marketing: true }); setSaved(false); }}
              className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-xl font-bold hover:bg-gray-50 transition-colors"
            >
              {isDE ? 'Alle akzeptieren' : 'Accept All'}
            </button>
            <button
              onClick={() => { setPrefs({ necessary: true, analytics: false, marketing: false }); setSaved(false); }}
              className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-xl font-bold hover:bg-gray-50 transition-colors"
            >
              {isDE ? 'Nur notwendige' : 'Necessary Only'}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookieSettings;